import React, { memo, useCallback } from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, palette, spaces } from '../../../../../assets/styles/themes/tokens';
import { FontSizeOption } from '../ContextMenu';
import { useTranslation } from 'react-i18next';
  
interface FontSizePickerProps {
    options: FontSizeOption[];
    anchorEl: HTMLElement | null;
    handleClick: (event: React.MouseEvent<HTMLElement>) => void;
    handleClose: () => void;
    handleFontSizeChange: (size: string) => void;
    value: string;
}

const FontSizePicker: React.FC<FontSizePickerProps> = ({ 
  options,
  anchorEl,
  handleClick,
  handleClose,
  handleFontSizeChange, 
  value 
}) => {
  const { t } = useTranslation();

  const currentValue = options.find(size => size.value === value);
  const currentSizeLabel = currentValue?.label.split(' ')[0] || 'Medium';

  const selectSize = useCallback((size: string) => () => {
    handleFontSizeChange(size);
    handleClose();
  }, [handleFontSizeChange]);
      
  return (
    <>
      <Button
        data-testid='font-size-picker-button'
        size='small'
        variant='text'
        color='secondary'
        endIcon={<ArrowDropDown />}
        onClick={handleClick}
        sx={{
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.06),
          },
        }}
      >
        {t(currentSizeLabel)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{
          style: {
            padding: spaces.xxs,
            borderRadius: borderRadius.sm,
            backgroundColor: 'white',
            border: `1px solid ${palette.divider}`,
          },
        }}
        PaperProps={{
          style: {
            boxShadow: 'none',
            borderRadius: borderRadius.sm,
          },
        }}
      >
        {options.map((size) => (
          <MenuItem
            data-testid={size.label}
            selected={value === size.value}
            key={size.label}
            onClick={selectSize(size.value)}
            sx={{ borderRadius: borderRadius.sm }}
          >
            <Typography 
              sx={{ 
                fontWeight: 400, 
                color: palette.text.primary, 
                fontSize: size.value 
              }}
            >
              {size.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default memo(FontSizePicker);
